"use strict";
/**
 * Secret redaction for every line @owlcat/logger writes (stdout + dev file).
 *
 * Twin of the workers' app/core/redaction.py. Dev run 10 (2026-11-01): an HTTP client error
 * embedded a request URL with `?key=` and the API key went to the logs. Call sites must
 * authenticate by header; this is the second barrier so a URL, an error message / stack or a
 * context field never carries a secret to a log.
 *
 * Scrubs:
 * - known secret VALUES: env vars whose NAME matches KEY|TOKEN|SECRET|PASSWORD|CREDENTIAL|DSN
 *   (values shorter than 8 chars ignored);
 * - URL query params key/api_key/token/access_token/sig/signature/... ;
 * - `Authorization: x`, `Bearer x`, `x-goog-api-key: x`, URL userinfo passwords;
 * - well-known key shapes (Google AIza…, OpenAI sk-…, xAI xai-…, Telegram bot<id>:<token>, Stripe);
 * - object values under a secret-named key ({ apiKey: … }).
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.MASK = void 0;
exports.refreshKnownSecrets = refreshKnownSecrets;
exports.redact = redact;
exports.redactValue = redactValue;
exports.MASK = '***';
const MIN_SECRET_LEN = 8;
const SECRET_NAME = /(key|token|secret|password|passwd|credential|signature|connection_?string|dsn)/i;
const NOT_SECRET_NAME = /(_id|_path|_file|_prefix|_key_id|keyid|publishable_key|public_key|_max_tokens?)$/i;
// Object FIELDS masked whole. Narrower than env names: a bare `key` / `objectKey` is a storage
// key, `tokens` a count — those stay readable.
const SECRET_FIELD = /^(authorization|cookie|set-cookie|x-goog-api-key|x-api-key|token|.*(api[_-]?key|secret|password|passwd|access[_-]?token|refresh[_-]?token|id[_-]?token|private[_-]?key|access[_-]?key|bot[_-]?token|session[_-]?token|auth[_-]?token))$/i;
// A plain word ("production", "enabled") under a *_TOKEN_* name is config, not a secret.
const PLAIN_WORD = /^[a-z_.-]+$|^\d+$/;
const QUERY_PARAMS = [
    'key', 'api_key', 'apikey', 'api-key', 'token', 'access_token', 'refresh_token', 'id_token',
    'auth', 'sig', 'signature', 'client_secret', 'secret', 'password', 'x-amz-signature',
    'x-amz-credential', 'x-amz-security-token', 'x-goog-signature', 'x-goog-credential',
];
const esc = (s) => s.replace(/[.*+?^${}()|[\]\\-]/g, '\\$&');
const PATTERNS = [
    [new RegExp(`([?&](?:${QUERY_PARAMS.map(esc).join('|')})=)[^&\\s"'#<>]+`, 'gi'), `$1${exports.MASK}`],
    [/((?:authorization|x-goog-api-key|x-api-key|api-key|xi-api-key)["']?\s*[:=]\s*["']?(?:bearer\s+|basic\s+|token\s+)?)[^\s"',;}]+/gi, `$1${exports.MASK}`],
    [/((?:SharedAccessKey|AccountKey|Password|Pwd)=)[^;\s"']+/gi, `$1${exports.MASK}`],
    [/(\bbearer\s+)[A-Za-z0-9._~+/=-]{8,}/gi, `$1${exports.MASK}`],
    [/(\b[a-z][a-z0-9+.-]*:\/\/[^:/\s@]+:)[^@\s/]+(@)/gi, `$1${exports.MASK}$2`],
    [/AIza[0-9A-Za-z_-]{30,}/g, exports.MASK],
    [/\bsk-(?:proj-|ant-)?[A-Za-z0-9_-]{20,}/g, exports.MASK],
    [/\bxai-[A-Za-z0-9_-]{20,}/g, exports.MASK],
    [/\b(?:sk|rk)_(?:live|test)_[A-Za-z0-9]{16,}/g, exports.MASK],
    [/\bwhsec_[A-Za-z0-9]{16,}/g, exports.MASK],
    [/(\bbot\d{5,}:)[A-Za-z0-9_-]{25,}/g, `$1${exports.MASK}`],
    [/\bre_[A-Za-z0-9]{8,}_[A-Za-z0-9_]{8,}/g, exports.MASK],
];
let secretValues = [];
function isSecretName(name) {
    return SECRET_NAME.test(name) && !NOT_SECRET_NAME.test(name);
}
/** (Re)load the secret VALUES to scrub from the environment (+ `extra`). Longest first. */
function refreshKnownSecrets(extra = []) {
    const out = new Set();
    const add = (v) => {
        const t = v.trim();
        if (t.length >= MIN_SECRET_LEN && !PLAIN_WORD.test(t))
            out.add(t);
    };
    const env = typeof process !== 'undefined' && process.env ? process.env : {};
    for (const [name, value] of Object.entries(env)) {
        if (typeof value !== 'string' || !isSecretName(name))
            continue;
        add(value);
        for (const part of value.split(/[,\s]+/)) {
            add(part);
            const i = part.indexOf(':');
            if (i >= 0)
                add(part.slice(i + 1));
        }
    }
    for (const v of extra)
        if (typeof v === 'string')
            add(v);
    secretValues = [...out].sort((a, b) => b.length - a.length);
}
/** Scrub every known secret value and secret-shaped substring from `text`. */
function redact(text) {
    if (!text)
        return text;
    let s = text;
    for (const v of secretValues)
        if (s.includes(v))
            s = s.split(v).join(exports.MASK);
    for (const [re, repl] of PATTERNS)
        s = s.replace(re, repl);
    return s;
}
/** Deep-redact strings in objects/arrays; Errors become their redacted `message\nstack`. */
function redactValue(value, depth = 0, seen = new WeakSet()) {
    if (typeof value === 'string')
        return redact(value);
    if (value instanceof Error)
        return redact(`${value.message}\n${value.stack ?? ''}`);
    if (value === null || typeof value !== 'object' || depth > 12)
        return value;
    if (seen.has(value))
        return '[Circular]';
    seen.add(value);
    if (Array.isArray(value))
        return value.map((v) => redactValue(v, depth + 1, seen));
    if (value instanceof Date)
        return value;
    const out = {};
    for (const [k, v] of Object.entries(value)) {
        out[k] = SECRET_FIELD.test(k) && typeof v === 'string' && v ? exports.MASK : redactValue(v, depth + 1, seen);
    }
    return out;
}
refreshKnownSecrets();
